export type SnapshotGridFixedColumnKey = "rowNumber" | "selection" | "actions" | "name";

interface SnapshotGridFixedColumn {
  key: SnapshotGridFixedColumnKey;
  width: number;
  left: number;
}

interface SnapshotGridLayoutParams {
  promptColumnCount: number;
  noteColumnCount: number;
  hasAddNoteColumn: boolean;
  showSelectionColumn?: boolean;
  showActionsColumn?: boolean;
  scrollableColumnIds?: string[];
  columnWidths?: SnapshotGridColumnWidths;
}

export interface SnapshotGridFixedColumnsVisibility {
  showSelectionColumn?: boolean;
  showActionsColumn?: boolean;
}

export const SNAPSHOT_GRID_DATA_COLUMN_WIDTH = 300;
export const SNAPSHOT_GRID_MIN_DATA_COLUMN_WIDTH = 160;
export const SNAPSHOT_GRID_MAX_DATA_COLUMN_WIDTH = 900;
export const SNAPSHOT_GRID_ADD_NOTE_COLUMN_WIDTH = 52;
export const SNAPSHOT_GRID_NAME_COLUMN_ID = "fixed:name";
export const SNAPSHOT_GRID_NAME_COLUMN_WIDTH = 250;
export const SNAPSHOT_GRID_MIN_NAME_COLUMN_WIDTH = 250;
export const SNAPSHOT_GRID_MAX_NAME_COLUMN_WIDTH = 900;
export type SnapshotGridColumnWidths = Record<string, number>;

export const SNAPSHOT_GRID_FIXED_COLUMNS = [
  { key: "rowNumber", width: 40, left: 0 },
  { key: "selection", width: 52, left: 40 },
  { key: "actions", width: 126, left: 92 },
  { key: "name", width: SNAPSHOT_GRID_NAME_COLUMN_WIDTH, left: 218 },
] as const satisfies readonly SnapshotGridFixedColumn[];

export const getSnapshotGridColumnStyle = (width: number) => ({
  width,
  minWidth: width,
  maxWidth: width,
});

const clampColumnWidth = (width: unknown, fallback: number, min: number, max: number) => {
  if (typeof width !== "number" || !Number.isFinite(width)) return fallback;
  return Math.min(max, Math.max(min, Math.round(width)));
};

export const getSnapshotGridNameColumnWidth = (columnWidths: SnapshotGridColumnWidths = {}) =>
  clampColumnWidth(
    columnWidths[SNAPSHOT_GRID_NAME_COLUMN_ID],
    SNAPSHOT_GRID_NAME_COLUMN_WIDTH,
    SNAPSHOT_GRID_MIN_NAME_COLUMN_WIDTH,
    SNAPSHOT_GRID_MAX_NAME_COLUMN_WIDTH,
  );

export const getSnapshotGridColumnWidth = (columnId: string, columnWidths: SnapshotGridColumnWidths = {}) => {
  if (columnId === SNAPSHOT_GRID_NAME_COLUMN_ID) return getSnapshotGridNameColumnWidth(columnWidths);

  return clampColumnWidth(
    columnWidths[columnId],
    SNAPSHOT_GRID_DATA_COLUMN_WIDTH,
    SNAPSHOT_GRID_MIN_DATA_COLUMN_WIDTH,
    SNAPSHOT_GRID_MAX_DATA_COLUMN_WIDTH,
  );
};

export const getSnapshotGridScrollableColumnsWidth = (
  columnIds: string[],
  columnWidths: SnapshotGridColumnWidths = {},
) => columnIds.reduce((total, columnId) => total + getSnapshotGridColumnWidth(columnId, columnWidths), 0);

const getVisibleSnapshotGridFixedColumns = (
  { showSelectionColumn = true, showActionsColumn = true }: SnapshotGridFixedColumnsVisibility = {},
  columnWidths?: SnapshotGridColumnWidths,
): SnapshotGridFixedColumn[] => {
  const nameWidth = getSnapshotGridNameColumnWidth(columnWidths);
  let left = 0;
  return SNAPSHOT_GRID_FIXED_COLUMNS.filter(
    column => (column.key !== "selection" || showSelectionColumn) && (column.key !== "actions" || showActionsColumn),
  ).map(column => {
    const width = column.key === "name" ? nameWidth : column.width;
    const visibleColumn = { ...column, width, left };
    left += width;
    return visibleColumn;
  });
};

export const getSnapshotGridFixedColumnStyle = (
  key: SnapshotGridFixedColumnKey,
  visibility?: SnapshotGridFixedColumnsVisibility,
  columnWidths?: SnapshotGridColumnWidths,
) => {
  const column = getVisibleSnapshotGridFixedColumns(visibility, columnWidths).find(candidate => candidate.key === key);

  if (!column) return getSnapshotGridColumnStyle(0);

  return {
    ...getSnapshotGridColumnStyle(column.width),
    left: column.left,
  };
};

export const getSnapshotGridLayout = ({
  promptColumnCount,
  noteColumnCount,
  hasAddNoteColumn,
  showSelectionColumn,
  showActionsColumn,
  scrollableColumnIds,
  columnWidths,
}: SnapshotGridLayoutParams) => {
  const fixedColumns = getVisibleSnapshotGridFixedColumns({ showSelectionColumn, showActionsColumn }, columnWidths);
  const fixedWidth = fixedColumns.reduce((total, column) => total + column.width, 0);
  const scrollableWidth =
    (scrollableColumnIds
      ? getSnapshotGridScrollableColumnsWidth(scrollableColumnIds, columnWidths)
      : (promptColumnCount + noteColumnCount) * SNAPSHOT_GRID_DATA_COLUMN_WIDTH) +
    (hasAddNoteColumn ? SNAPSHOT_GRID_ADD_NOTE_COLUMN_WIDTH : 0);

  return {
    fixedColumns,
    fixedWidth,
    scrollableWidth,
    tableWidth: fixedWidth + scrollableWidth,
  };
};
